import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, Loader2, RotateCcw } from 'lucide-react';
import type { SetOccurrenceStatusInput, TaskReviewItem } from '@pms/shared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatDate, formatRelativeTime } from '@/lib/dateFormat';
import { tasksService } from '@/services/tasks.service';
import { getErrorMessage } from '@/lib/errors';
import { TaskStatusBadge, TaskTypeBadge } from './TaskBadges';
import { StatusRemarkModal } from './StatusRemarkModal';
import type { VoiceNoteDraft } from './VoiceNoteRecorder';
import { fullName, initials } from './taskConstants';

interface TaskReviewModalProps {
  item: TaskReviewItem | null;
  onOpenChange: (open: boolean) => void;
}

/** Label left, value right — matching the task drawer's meta card. */
function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="shrink-0 text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-sm text-foreground">{children}</span>
    </div>
  );
}

/**
 * The reviewer's verdict on one submission: approve it, or send it back.
 *
 * Done acts immediately. Need Improvement hands off to `StatusRemarkModal`, which
 * collects the mandatory explanation — the assignee has to know what to fix.
 *
 * Mounted only while open (the caller passes `item` as null to close), so each opening
 * starts clean without reset effects.
 */
export function TaskReviewModal({ item, onOpenChange }: TaskReviewModalProps) {
  const qc = useQueryClient();
  const [remarkOpen, setRemarkOpen] = useState(false);

  const review = useMutation({
    mutationFn: (v: { input: SetOccurrenceStatusInput; voice?: VoiceNoteDraft | null }) =>
      tasksService.setStatusWithVoice(item!.id, v.input, v.voice ?? null),
    onSuccess: (result, v) => {
      // A failed recording must not read as a failed review — the verdict already stands.
      if (result.voiceError) {
        toast.warning(`Sent back, but the voice note failed to upload. (${result.voiceError})`);
      } else {
        toast.success(
          v.input.status === 'completed'
            ? 'Task approved and marked completed'
            : 'Sent back to the assignee for improvement',
        );
      }
      qc.invalidateQueries({ queryKey: ['tasks'] });
      setRemarkOpen(false);
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!item) return null;
  const busy = review.isPending;

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onOpenChange(false); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="space-y-1.5 pr-6">
          <DialogTitle className="text-base leading-tight">{item.task_title}</DialogTitle>
          {/* Badges on their own line: a long title used to squeeze them off the edge.
              is_priority already comes down with the row and was going unused. */}
          <div className="flex flex-wrap items-center gap-1.5">
            <TaskTypeBadge type={item.task_type} />
            <TaskStatusBadge status="for_review" />
            {item.is_priority && (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-400">
                Priority
              </span>
            )}
          </div>
          <DialogDescription className="text-xs">
            Approve this submission, or send it back for improvement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5">
          {/* The assignee is the subject of a review, so they get the teal surface and
              everything else stays neutral. Previously this and the description shared
              one muted grey, which made the two read as a single block. */}
          <div className="flex items-center gap-2.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
            {item.photo_url ? (
              <img
                src={item.photo_url}
                alt=""
                className="h-9 w-9 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                {initials(item.first_name, item.last_name)}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-tight text-foreground">
                {fullName(item.first_name, item.last_name)}
              </p>
              <p className="truncate text-[11px] leading-tight text-muted-foreground">
                {[item.employee_code, item.department_name].filter(Boolean).join(' · ') || 'Assignee'}
              </p>
            </div>
          </div>

          {/* Labelled and on a card: unlabelled grey text in a rounded box read as a
              disabled input rather than as the task's own description. */}
          {item.task_description && (
            <div className="rounded-lg border border-border/60 bg-card px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Task Description
              </p>
              <p className="mt-0.5 whitespace-pre-wrap break-words text-sm leading-snug text-foreground">
                {item.task_description}
              </p>
            </div>
          )}

          <div className="divide-y divide-border/40 rounded-lg border border-border/60 bg-card px-3">
            <MetaRow label="Assigned by">
              {fullName(item.created_by_first_name, item.created_by_last_name)}
            </MetaRow>
            <MetaRow label="For date">{formatDate(item.occurrence_date)}</MetaRow>
            <MetaRow label="Submitted">
              {item.submitted_at ? formatRelativeTime(item.submitted_at) : '—'}
            </MetaRow>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => setRemarkOpen(true)}
          >
            <RotateCcw className="h-4 w-4" />
            Need Improvement
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={() => review.mutate({ input: { status: 'completed' } })}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Done
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Mounted only while open, like the other callers — so a cancelled recording is
          gone the next time it opens, with no reset effect. */}
      {remarkOpen && (
        <StatusRemarkModal
          status="needs_improvement"
          open
          onOpenChange={(o) => { if (!o) setRemarkOpen(false); }}
          busy={busy}
          onConfirm={(note, voice) => review.mutate({ input: { status: 'needs_improvement', note }, voice })}
        />
      )}
    </Dialog>
  );
}
